import type { AdminUserProvisionInput } from '@atlas/models'
import type { DTOOf, IUser, JobFunction, UserAdminFields, UserDirectoryDTO, UserRole } from '@atlas/types'
import { apiClient } from './api.client'

/**
 * User-administration API client (ADR-0013). Reuses the shared Axios client
 * and its auth/error handling — no second client, no general retries.
 * Responses omit `cognitoSub` and all credential/token material. The same
 * directory serves the Work Order assignee picker and author/actor display-name
 * lookups. Role/status use the dedicated admin endpoints — never the
 * profile-update route.
 */

/**
 * A user as the directory returns it. The identity fields are always there; the
 * administrative ones arrive only for `ADMIN` and above, because the server
 * narrows the payload below that (ADR-0031).
 *
 * They are optional rather than a separate type so that a surface reading
 * `email` or `role` has to say what it does when they are absent, instead of
 * trusting a shape the server may not have sent.
 */
export type UserDTO = UserDirectoryDTO & Partial<Pick<DTOOf<IUser>, UserAdminFields>>

export const userApi = {
	// GET /users → 200 array | 204 empty. Normalize 204's empty body to [].
	// user-service always returns a bare array (or an empty 204 body) — never
	// an enveloped `{ data: [...] }` shape — so no further normalization is needed.
	listUsers: () => apiClient.get<UserDTO[]>('/users').then((r) => (Array.isArray(r.data) ? r.data : [])),

	// GET /users/active-pms: every active PM, unpaged, for the PM pickers.
	listActivePms: () => apiClient.get<UserDTO[]>('/users/active-pms').then((r) => (Array.isArray(r.data) ? r.data : [])),

	// POST /admin/users — exact strict contract { email, displayName, role }.
	provisionUser: (body: AdminUserProvisionInput) => apiClient.post<UserDTO>('/admin/users', body).then((r) => r.data),

	// PATCH /admin/users/:userId/role — dedicated role endpoint, { role } only.
	changeUserRole: (userId: string, role: UserRole) =>
		apiClient.patch<UserDTO>(`/admin/users/${userId}/role`, { role }).then((r) => r.data),

	// PATCH /admin/users/:userId/job-function — dedicated endpoint, { jobFunction } only.
	changeUserJobFunction: (userId: string, jobFunction: JobFunction) =>
		apiClient.patch<UserDTO>(`/admin/users/${userId}/job-function`, { jobFunction }).then((r) => r.data),

	// PATCH /admin/users/:userId/status — dedicated status endpoint, { isActive } only.
	changeUserStatus: (userId: string, isActive: boolean) =>
		apiClient.patch<UserDTO>(`/admin/users/${userId}/status`, { isActive }).then((r) => r.data),
}
